// import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { User, OvertimeRequest } from '@/types';
import { EmployeeStatus } from '@/contexts/employees/EmployeesContext';
import { OvertimeStatus, UserRole } from '@/types/enums';
// Logger import removed as it's not being used;

/**
 * Hook for overtime data retrieval functionality
 */
export const useDataRetrieval = (
  user: { id: string; name?: string; role?: string; department?: string[] } | null,
  overtimeRequests: OvertimeRequest[],
) => {  
  const getOvertimeRequestById = (id: string) => {
    return overtimeRequests.find(request => request.id === id) || null;
  };
  
  const getUserRequests = () => {
    if (!user || !user.id) {
      return [];
    }
    const userRole = user.role?.toLowerCase();
    // For department_head, superintendent, and plant_manager, show all requests created by their role
    if (userRole === UserRole.DEPARTMENT_HEAD || userRole === UserRole.SUPERINTENDENT || userRole === UserRole.PLANT_MANAGER) {
      return overtimeRequests.filter(request =>
        (request.created_by_role?.toLowerCase() === userRole) ||
        request.employee_id === user.id ||
        request.employeeId === user.id ||
        request.submittedById === user.id
      );
    }
    // Default: only requests where the user is the creator
    return overtimeRequests.filter(request =>
      request.employee_id === user.id ||
      request.employeeId === user.id ||
      request.submittedById === user.id
    );
  };
  
  const getPendingRequests = () => {
    if (!user) return [];

    if (user?.role === UserRole.SUPER_ADMIN) {
      // Super admin should see pending_vp_approval requests (threshold exceeded)
      return overtimeRequests.filter(request =>
        request.status === OvertimeStatus.PENDING_VP_APPROVAL
      );
    }

    if (user?.role === UserRole.PLANT_MANAGER) {
      // Plant manager sees pending_pm_approval requests (superintendent workflow) 
      // and legacy pending requests that don't require VP approval
      // BUT ONLY from their assigned departments
      if (!user.department || user.department.length === 0) return []; // No departments, no requests
      
      return overtimeRequests.filter(request =>
        user.department.includes(request.department) && (
          (request.status?.toLowerCase() === OvertimeStatus.PENDING_PM_APPROVAL) ||
          (request.status?.toLowerCase() === OvertimeStatus.PENDING && !request.vpApprovalRequired)
        )
      );
    }

    if (user?.role === UserRole.DEPARTMENT_HEAD) {
      // Department head sees pending_dh_approval requests (superintendent workflow) 
      // and legacy pending requests from their departments
      return overtimeRequests.filter(request =>
        user.department?.includes(request.department) && // request.department is string
        (request.status?.toLowerCase() === OvertimeStatus.PENDING_DH_APPROVAL ||
         request.status?.toLowerCase() === OvertimeStatus.PENDING)
      );
    }

    if (user?.role === UserRole.SUPERINTENDENT) {
      // Superintendents don't approve requests - they only create them
      return [];
    }

    return [];
  };
  
  const getDepartmentRequests = () => {
    if (!user || !user.department || user.department.length === 0) return [];
    // Include all requests from any of the user's departments, regardless of status or threshold
    return overtimeRequests.filter(request => 
      user.department?.includes(request.department) // request.department is string
    );
  };
  
  // ✅ REMOVED: getDashboardStats - now handled by consolidated analytics utils
  
  const getDepartmentEmployees = async (departmentId: string): Promise<User[]> => {
    try {
      const { data, error } = await supabase
        .from('employees')
        .select('*')
        .eq('department', departmentId)
        .eq('status', EmployeeStatus.ACTIVE);
      
      if (error) {
        return [];
      }
      
      if (data && data.length > 0) {
        const mappedEmployees = data.map((emp: any) => ({
          id: emp.id,
          name: `${emp.first_name} ${emp.last_name}`,
          firstName: emp.first_name,
          lastName: emp.last_name,
          email: emp.email,
          role: emp.role,
          department: emp.department,
          position: emp.position,
          shift: emp.shift,
          status: emp.status,
          residency: emp.residency,
          isActive: emp.status === EmployeeStatus.ACTIVE
        }));
        return mappedEmployees;
      } else {
        return [];
      }
    } catch {
      return [];
    }
  };
  
  const getAllEmployees = async (): Promise<User[]> => {
    try {
      const { data, error } = await supabase
        .from('employees')
        .select('*')
        .eq('status', EmployeeStatus.ACTIVE);
      
      if (error) {
        return [];
      }
      
      if (data && data.length > 0) {
        const mappedEmployees = data.map((emp: any) => ({
          id: emp.id,
          name: `${emp.first_name} ${emp.last_name}`,
          firstName: emp.first_name,
          lastName: emp.last_name,
          email: emp.email,
          role: emp.role,
          department: emp.department,
          position: emp.position,
          shift: emp.shift,
          status: emp.status,
          residency: emp.residency,
          isActive: emp.status === EmployeeStatus.ACTIVE
        }));
        return mappedEmployees;
      } else {
        return [];
      }
    } catch {
      return [];
    }
  };

  return {
    getOvertimeRequestById,
    getUserRequests,
    getPendingRequests,
    getDepartmentRequests,
    getDepartmentEmployees,
    getAllEmployees
  };
}